import React, { useEffect, useRef, useState } from "react";
import { Currency, ProductInterface, User } from "../../types/common.types";
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  CircularProgress,
  Typography,
} from "@mui/material";
import { get } from "lockr";
import { useTranslation } from "../../hooks/useTranslation";
import { getProductAnalytics } from "../../helpers/xhr";
interface SalesInterface {
  product: ProductInterface;
}

interface ProductAnalytics {
  totalSalesRevenue: number;
  totalUnitsSold: number;
  numberOfOrders: number;
  averageOrderValue: number;
}

const analyticsLabels: Record<keyof ProductAnalytics, string> = {
  totalSalesRevenue: "Total Sales Revenue",
  totalUnitsSold: "Total Units Sold",
  numberOfOrders: "Number of Orders",
  averageOrderValue: "Average Order Value",
};

const SalesTab = (props: SalesInterface): JSX.Element => {
  const { product } = props;

  const [analytics, setAnalytics] = useState<ProductAnalytics | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const fetched = useRef(false);

  const productInStock = useTranslation("product_is_in_stock");
  const productOutOfStock = useTranslation("out_of_stock");

  const productHasPhoto =
    product && product.productPhoto && product.productPhoto.secureUrl;

  let defaultLanguage = "en";
  let currency: Currency = { name: "", prefix: "" };
  const userData = get("user") as User | undefined;
  if (userData) {
    defaultLanguage = userData.defaultLanguage;
    currency = userData.currency;
  }
  const productPrice = product.prices["regular"];

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;

    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const response = await getProductAnalytics(product.id);
        setAnalytics(response.data);
        setError(null);
      } catch (err) {
        setError("Failed to load analytics. Please try again later.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [product.id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <CircularProgress sx={{ color: "#9e4a50" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={3}>
        <Alert
          severity="error"
          sx={{ bgcolor: "rgba(248, 113, 113, 0.12)", color: "#f87171" }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!analytics) {
    return (
      <Typography sx={{ color: "#7a6560", textAlign: "center", mt: 2 }}>
        No analytics available.
      </Typography>
    );
  }

  return (
    <Box py={1.5}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 260px" },
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gap: 1,
            alignContent: "start",
          }}
        >
          {Object.entries(analytics).map(([key, value]) => (
            <Box
              key={key}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                p: 1.25,
                borderRadius: "8px",
                border: "1px solid rgba(131, 60, 65, 0.16)",
                bgcolor: "rgba(31, 22, 21, 0.72)",
              }}
            >
              <Typography sx={{ color: "#7a6560", fontSize: "12px" }}>
                {analyticsLabels[key as keyof ProductAnalytics] || key}
              </Typography>
              <Typography
                sx={{ color: "#f0ece8", fontSize: "13px", fontWeight: 700 }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          flex={1}
          p={1.5}
          borderRadius="8px"
          border="1px solid rgba(131, 60, 65, 0.16)"
          bgcolor="rgba(31, 22, 21, 0.72)"
        >
          <Typography
            sx={{
              color: "#f0ece8",
              fontSize: "16px",
              fontWeight: 700,
              mb: 1,
              textAlign: "center",
            }}
          >
            {product.names[defaultLanguage]}
          </Typography>
          <Card
            sx={{
              maxWidth: 210,
              width: "100%",
              borderRadius: "8px",
              position: "relative",
              bgcolor: "#16100f",
              border: "1px solid rgba(131, 60, 65, 0.18)",
              boxShadow: "none",
            }}
          >
            <CardActionArea sx={{ padding: 1, borderRadius: "8px" }}>
              {productHasPhoto && (
                <CardMedia
                  component="img"
                  image={product?.productPhoto?.secureUrl}
                  alt="product"
                  sx={{
                    width: "100%",
                    height: 150,
                    objectFit: "cover",
                    borderRadius: "7px",
                  }}
                />
              )}
              <CardContent sx={{ padding: 1 }}>
                <Typography
                  sx={{
                    color: "#7a6560",
                    fontSize: "12px",
                    textAlign: "center",
                    pt: 1,
                  }}
                >
                  Current Price: {`${currency.prefix}${productPrice}`}
                </Typography>
              </CardContent>
            </CardActionArea>
            <CardActions sx={{ position: "absolute", top: 0, right: 0 }}>
              <Typography
                variant="body2"
                bgcolor={
                  product.inStock
                    ? "rgba(74, 222, 128, 0.13)"
                    : "rgba(248, 113, 113, 0.12)"
                }
                sx={{
                  color: product.inStock ? "#4ade80" : "#f87171",
                  fontSize: 10,
                  px: 1,
                  py: "5px",
                  fontWeight: "500",
                  borderTopRightRadius: "8px",
                  borderBottomLeftRadius: "8px",
                }}
              >
                {product.inStock ? productInStock : productOutOfStock}
              </Typography>
            </CardActions>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default SalesTab;
